import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { api, errMsg, isoDay } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusPill, Field } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); x.setHours(12, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const NEXT = { pending: "in_progress", in_progress: "done", done: "pending" };

const TaskForm = ({ open, onOpenChange, team, isEmployer, onSaved }) => {
  const [f, setF] = useState({ title: "", description: "", staff_id: "", date: isoDay(new Date()), start_time: "09:00", end_time: "10:00", priority: "normal" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.post("/tasks", { ...f, staff_id: f.staff_id || null }); toast.success("Task scheduled"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEmployer ? "Assign a task" : "Add a task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isEmployer && (
            <Field label="Staff member">
              <select className="dx-input" value={f.staff_id} onChange={set("staff_id")} data-testid="task-staff-select">
                <option value="">Choose…</option>
                {team.map((t) => <option key={t.staff_id} value={t.staff_id}>{t.staff_name} - {t.job_title}</option>)}
              </select>
            </Field>
          )}
          <Field label="Title"><input className="dx-input" value={f.title} onChange={set("title")} data-testid="task-title-input" /></Field>
          <Field label="Details"><textarea rows={2} className="dx-input" value={f.description} onChange={set("description")} data-testid="task-description-input" /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Date"><input type="date" className="dx-input" value={f.date} onChange={set("date")} data-testid="task-date-input" /></Field>
            <Field label="Start"><input type="time" className="dx-input" value={f.start_time} onChange={set("start_time")} data-testid="task-start-input" /></Field>
            <Field label="End"><input type="time" className="dx-input" value={f.end_time} onChange={set("end_time")} data-testid="task-end-input" /></Field>
          </div>
          <Field label="Priority">
            <select className="dx-input" value={f.priority} onChange={set("priority")} data-testid="task-priority-select">
              {["low", "normal", "high"].map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <button className="dx-btn-gold w-full" onClick={save} disabled={f.title.length < 2 || (isEmployer && !f.staff_id)} data-testid="task-save-btn">Save task</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Tasks() {
  const { user } = useAuth();
  const isEmployer = user.role === "employer";
  const [week, setWeek] = useState(monday(new Date()));
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));

  const load = () => api.get("/tasks", { params: { start: isoDay(days[0]), end: isoDay(days[6]) } }).then((r) => setTasks(r.data));
  useEffect(() => { load(); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isEmployer) api.get("/employer/team").then((r) => setTeam(r.data)); }, [isEmployer]);

  const cycle = async (t) => { try { await api.patch(`/tasks/${t.id}`, { status: NEXT[t.status] }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (t) => { await api.delete(`/tasks/${t.id}`); load(); };

  return (
    <div>
      <PageHeader eyebrow="Schedules" title="Task schedule" subtitle={isEmployer ? "Plan the week for your team. Staff update progress from their phones." : "Your tasks for the week. Tap a status to update it."}
        action={user.role !== "admin" && <button className="dx-btn-gold" onClick={() => setOpen(true)} data-testid="task-add-btn"><Plus className="w-4 h-4" />{isEmployer ? "Assign task" : "Add task"}</button>} />
      <div className="flex items-center gap-2 mb-4">
        <button className="dx-btn-ghost px-3" onClick={() => setWeek(addDays(week, -7))} data-testid="task-prev-week"><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-semibold text-sm" data-testid="task-week-label">{days[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} - {days[6].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
        <button className="dx-btn-ghost px-3" onClick={() => setWeek(addDays(week, 7))} data-testid="task-next-week"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {days.map((d) => {
          const key = isoDay(d);
          const list = tasks.filter((t) => t.date === key);
          const today = key === isoDay(new Date());
          return (
            <div key={key} className={`rounded-2xl p-3 min-h-[140px] ${today ? "bg-[#5B2A6E] text-white" : "bg-white border"}`} data-testid={`task-day-${key}`}>
              <div className={`text-xs font-bold uppercase tracking-wider ${today ? "text-[#D4AF37]" : "text-[#8a7a92]"}`}>{d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" })}</div>
              <div className="mt-2 space-y-2">
                {list.map((t) => (
                  <div key={t.id} className="rounded-xl bg-white text-[#250E31] border p-2.5 text-xs" data-testid={`task-${t.id}`}>
                    <div className="flex justify-between gap-1">
                      <span className={`font-semibold text-sm ${t.status === "done" ? "line-through opacity-60" : ""}`}>{t.title}</span>
                      {t.assigned_by === user.id && <button onClick={() => remove(t)} aria-label="Delete" data-testid={`task-${t.id}-delete`}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600" /></button>}
                    </div>
                    <div className="text-[#6b5a73] mt-0.5">{t.start_time}-{t.end_time}{isEmployer || user.role === "admin" ? ` · ${t.staff_name}` : t.assigned_by !== user.id ? ` · ${t.assigned_by_name}` : ""}</div>
                    {t.priority === "high" && <div className="text-rose-600 font-semibold mt-0.5">High priority</div>}
                    <button className="mt-1.5" onClick={() => cycle(t)} data-testid={`task-${t.id}-status-btn`}><StatusPill status={t.status} /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {open && <TaskForm open={open} onOpenChange={setOpen} team={team} isEmployer={isEmployer} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
